import { useState, useEffect } from 'react'
import { fetchAPI } from '@/api/client'

/**
 * Server-authoritative give-up window (Feature 1d) — mirrors cap-api
 * /api/cap/give-up-window. Per offer, since the gate closes at the offer's own reset.
 */
export interface GiveUpWindow {
  isOpen: boolean
  openHourUTC: number
  opensAtUTC: string
  resetsAtUTC: string
  nowUTC: string
  /** Zone the gate's hours and the reset are expressed in — 'UTC' for all but Reco. */
  reset_timezone?: string
  /** How many hours before the reset the gate opens (24 − openHour). */
  hoursBeforeReset?: number
}

export interface GiveUpWindowState {
  window: GiveUpWindow | null
  loading: boolean
  error: string | null
}

/**
 * Polls the server for whether "give up cap" is currently open — the last few hours of
 * the offer's own day, closing at its reset. The server is the source of truth and also
 * enforces the gate (403 outside the window); this drives the UI/countdown.
 */
export function useGiveUpWindow(pollMs = 60000, offerId?: number | null): GiveUpWindowState {
  const [state, setState] = useState<GiveUpWindowState>({ window: null, loading: true, error: null })

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        // `?offer=` scopes the gate to that offer's reset zone. Give-up closes AT the reset,
        // so an unscoped call would hand a Pacific offer the UTC day's open/close instants.
        const w = await fetchAPI<GiveUpWindow>(
          offerId ? `/api/cap/give-up-window?offer=${offerId}` : '/api/cap/give-up-window'
        )
        if (!cancelled) setState({ window: w, loading: false, error: null })
      } catch (err) {
        if (!cancelled) {
          setState((s) => ({ ...s, loading: false, error: err instanceof Error ? err.message : 'Failed to load window' }))
        }
      }
    }
    load()
    const id = setInterval(load, pollMs)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [pollMs, offerId])

  return state
}
